import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  numberAttribute,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbsenceOptionUnit } from '@app/enums';
import { DecimalToDurationFormatPipe, DurationFormatOptions } from '@app/pipes/decimal-to-duration-format.pipe';
import { Balance, BalanceExpiry, CorrectionItem } from '@app/reducers/orm/employee/employee.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KnowledgeBaseArticleLinkPipe } from '@sb/shared/knowledge-base';
import { DividerDirective } from '@sb/ui/divider';
import { IconComponent } from '@sb/ui/icon';
import { NoticeActionDirective, NoticeComponent, NoticeHeaderDirective } from '@sb/ui/notice';

import type { EmployeeOverviewData } from '../../employee-absence-overview.model';
import { LocaleDatePipe } from './../../../../../../pipes/locale-date.pipe';
import { TimeOffPartialBalanceComponent } from './time-off-partial-balance/time-off-partial-balance.component';
import {
  CorrectionType,
  type PartialBalance,
  type PartialBalanceItem,
} from './time-off-partial-balance/time-off-partial-balance.model';

interface BalanceExpiryEnhanced extends BalanceExpiry {
  formattedDate: string;
  expireAmount: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'time-off-balance',
  templateUrl: './time-off-balance.component.html',
  imports: [
    CommonModule,
    TranslatePipe,
    IconComponent,
    TimeOffPartialBalanceComponent,
    DecimalToDurationFormatPipe,
    NoticeComponent,
    NoticeActionDirective,
    NoticeHeaderDirective,
    DividerDirective,
  ],
  providers: [LocaleDatePipe, DecimalToDurationFormatPipe],
})
export class TimeOffBalanceComponent implements OnChanges {
  private localeDatePipe = inject(LocaleDatePipe);
  private translate = inject(TranslateService);
  private decimalToDurationFormat = inject(DecimalToDurationFormatPipe);
  private knowledgeBaseArticleLinkPipe = inject(KnowledgeBaseArticleLinkPipe);

  @Input()
  balance: Balance;

  @Input()
  balanceExpiries: BalanceExpiry[];

  expiries: BalanceExpiryEnhanced[];

  @Input({ transform: numberAttribute })
  year = new Date().getFullYear();

  @Input()
  isActive = false;

  @Input()
  employee: EmployeeOverviewData;

  @Output()
  toggled = new EventEmitter<void>();

  partialBalances: PartialBalance[];
  remainingPercentage = 0;
  durationOptions: DurationFormatOptions = {
    roundingRule: 'halfDays',
  };

  ngOnChanges(changes: SimpleChanges) {
    if (changes['balance'] || changes['year']) {
      this.setPartialBalances();
      this.remainingPercentage = this.balance.percentage;
    }
    if (changes['balanceExpiries']) {
      this.setUpComingExpiries();
    }
  }

  private setUpComingExpiries(): void {
    this.expiries = this.balanceExpiries
      ?.filter((expiry) => (this.balance.unit === AbsenceOptionUnit.DAYS ? expiry.expired >= 0.5 : true))
      ?.map((expiry) => ({
        ...expiry,
        expireAmount: this.parseValue(expiry.expired),
        formattedDate: this.localeDatePipe.transform(expiry.date, 'medium'),
      }));
  }

  private setPartialBalances() {
    this.partialBalances = [this.getAccrued(), this.getCorrected(), this.getUsed()];
  }

  private getAccrued(): PartialBalance {
    return {
      balanceId: this.balance.timeOffBalanceId,
      label: this.translate.instant('Accrued'),
      value: this.parseValue(this.balance.accrual.total, false),
      items: [
        {
          label: `${this.translate.instant('Carryover')} ${this.year - 1}`,
          value: this.parseValue(this.balance.accrual.carryover),
          sign: this.getSign(this.balance.accrual.carryover),
        },
        {
          label: this.translate.instant('Accrued'),
          value: this.parseValue(this.balance.accrual.accrued),
          sign: this.getSign(this.balance.accrual.accrued),
        },
        {
          label: this.translate.instant('Forecasted'),
          value: this.parseValue(this.balance.accrual.forecast),
          sign: this.getSign(this.balance.accrual.forecast),
        },
      ],
    };
  }

  private getCorrected(): PartialBalance {
    return {
      balanceId: this.balance.timeOffBalanceId,
      label: this.translate.instant('Corrected'),
      value: this.parseValue(
        this.balance.unit === AbsenceOptionUnit.DAYS
          ? this.balance.corrected.totalDays
          : this.balance.corrected.totalHours,
        false,
      ),
      items: this.correctionsAsPartialBalanceItems(),
      itemsAreCorrections: true,
    };
  }

  get expired(): PartialBalance {
    return {
      balanceId: this.balance.timeOffBalanceId,
      label: this.translate.instant('Expired'),
      value: `- ${this.parseValue(this.getExpiredTotal())}`,
      items: this.balance.expiries
        ?.filter((expiry) => (this.balance.unit === AbsenceOptionUnit.DAYS ? expiry.expired >= 0.5 : true))
        ?.map((expiry) => ({
          label: this.localeDatePipe.transform(expiry.date, 'compact'),
          value: `- ${this.parseValue(expiry.expired)}`,
        })),
      itemsAreCorrections: false,
    };
  }

  private getExpiredTotal(): number {
    return this.balance?.expiries?.reduce((total, expiry) => total + expiry.expired, 0);
  }

  private getUsed(): PartialBalance {
    // Used values are always negative
    return {
      balanceId: this.balance.timeOffBalanceId,
      label: this.translate.instant('Used'),
      value: this.parseValue(this.balance.used.total * -1, false),
      items: [
        {
          label: this.translate.instant('Taken'),
          value: this.parseValue(this.balance.used.taken),
          sign: this.balance.used.taken ? '-' : undefined,
        },
        {
          label: this.translate.instant('Pending'),
          value: this.parseValue(this.balance.used.pending),
          sign: this.balance.used.pending ? '-' : undefined,
        },
        {
          label: this.translate.instant('Wait hours'),
          value: this.parseValue(this.balance.used.waitHours),
          sign: this.balance.used.waitHours ? '-' : undefined,
        },
        {
          label: this.translate.instant('Pending wait hours'),
          value: this.parseValue(this.balance.used.pendingWaitHours),
          sign: this.balance.used.pendingWaitHours ? '-' : undefined,
        },
      ],
    };
  }

  private correctionsAsPartialBalanceItems(): PartialBalanceItem[] {
    return this.balance.corrected.corrections.map((correction) => {
      const correctionValue =
        this.balance.unit === AbsenceOptionUnit.DAYS ? correction.correctionDays : correction.correctionHours;

      return {
        label: this.localeDatePipe.transform(correction.date, 'compact'),
        value: this.parseValue(correctionValue),
        sign: this.getSign(correctionValue),
        moreDetails:
          this.balance.unit === AbsenceOptionUnit.DAYS && correction.pay
            ? this.parseValue(correction.correctionHours, true, AbsenceOptionUnit.HOURS)
            : null,
        correction: correction,
        correctionType: this.getCorrectionType(correction),
      };
    });
  }

  private getSign(value: number): '+' | '-' | undefined {
    return value === 0 ? undefined : value < 0 ? '-' : '+';
  }

  private parseValue(value: number, removeSign = true, unit?: AbsenceOptionUnit): string {
    return this.decimalToDurationFormat.transform(
      // Sign (+/-) is shown separately in PartialBalanceItem
      removeSign ? Math.abs(value) : value,
      unit || this.balance.unit,
      this.durationOptions,
    );
  }

  private getCorrectionType(correction: CorrectionItem): CorrectionType {
    if (correction.pay) {
      return CorrectionType.PAYOUT;
    }

    if (correction.note.includes('Transfer')) {
      return CorrectionType.TRANSFER;
    }

    if (correction.type === 'Time off balance cycle') {
      return CorrectionType.REACTIVATION;
    }

    return CorrectionType.CORRECTION;
  }

  redirectToHelpCenter() {
    const link = this.knowledgeBaseArticleLinkPipe.transform('expiring-absence-balances');
    window.open(link, '_blank');
  }
}
